import type { AxiosError } from "axios";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import useSWR from "swr";
import { AccordionCardWithEditFeatures } from "../../../../components/AccordionCard/AccordionCardwithEditFeatures";
import { useAuthContext } from "../../../../components/Auth";
import {
  RegularParagraph,
  SoftHeader,
} from "../../../../components/Typography/Typography";
import { TwoColumGrid } from "../../../../layout/portalPageLayout";
import type {
  AttributeTemplateSchema,
  PIMProduct,
} from "../../../../types/types.PIM";
import { useRoutePath } from "../../../../util/Routing";
import { useStoreState } from "../../../../util/util";
import { AttributeValueDisplay } from "./product_overview.utils";

const SoftHeaderWithMargin = styled(SoftHeader)`
  margin: 10px 0 5px;
`;

export const SchemaTagCard = ({
  template_id,
  product,
}: {
  template_id: string;
  product: PIMProduct;
}) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { roleIsSomeKindOfSeller, hasPermission } = useAuthContext();
  const { storePath } = useRoutePath();

  const { data: template } = useSWR<AttributeTemplateSchema, AxiosError>(
    hasPermission("view_templates")
      ? `/v2/tenants/${tenant_id}/pim/templates/${template_id}`
      : null
  );

  const product_url = `${window.location.origin}${storePath}/product/${product.slug}`;

  const default_name_tag = useMemo(() => {
    return product.product_schema.groups
      .flatMap(({ attributes }) => attributes)
      .find(({ object_type }) => object_type === "product_name");
  }, [product.product_schema.groups]);

  const default_description_tag = useMemo(() => {
    return product.product_schema.groups
      .flatMap(({ attributes }) => attributes)
      .find(({ object_type }) => object_type === "product_description");
  }, [product.product_schema.groups]);

  const tag_name_value = useMemo(() => {
    if (template?.schema_tags?.tag_name_attribute) {
      const { tag_name_attribute } = template.schema_tags;
      return product.product_schema.groups
        .flatMap(({ attributes }) => attributes)
        .find(
          ({ id, is_restricted, is_not_applicable }) =>
            id === tag_name_attribute.id && !is_restricted && !is_not_applicable
        );
    }
  }, [product.product_schema.groups, template?.schema_tags]);

  const tag_description_value = useMemo(() => {
    if (template?.schema_tags?.tag_description_attribute) {
      const { tag_description_attribute } = template.schema_tags;
      return product.product_schema.groups
        .flatMap(({ attributes }) => attributes)
        .find(
          ({ id, is_restricted, is_not_applicable }) =>
            id === tag_description_attribute.id &&
            !is_restricted &&
            !is_not_applicable
        );
    }
  }, [product.product_schema.groups, template?.schema_tags]);

  if (!template || !template.show_schema_tag) {
    return null;
  }

  return (
    <AccordionCardWithEditFeatures
      title={t("Schema Tags")}
      editing={false}
      expanded={true}
    >
      <TwoColumGrid>
        <div>
          <SoftHeaderWithMargin
            data-for="schema_tag_name"
            data-tip={
              roleIsSomeKindOfSeller
                ? tag_name_value?.description ?? default_name_tag?.description
                : undefined
            }
          >
            {t("Name")}
          </SoftHeaderWithMargin>
          {tag_name_value || default_name_tag ? (
            <AttributeValueDisplay
              attribute={tag_name_value ?? default_name_tag!}
              isPortfolio={false}
            />
          ) : (
            "--"
          )}
        </div>
        <div>
          <SoftHeaderWithMargin
            data-for="schema_tag_description"
            data-tip={
              roleIsSomeKindOfSeller
                ? tag_description_value?.description ??
                  default_description_tag?.description
                : undefined
            }
          >
            {t("Description")}
          </SoftHeaderWithMargin>
          {tag_description_value || default_description_tag ? (
            <AttributeValueDisplay
              attribute={tag_description_value ?? default_description_tag!}
              isPortfolio={false}
            />
          ) : (
            "--"
          )}
        </div>
        <div>
          <SoftHeaderWithMargin>{t("URL")}</SoftHeaderWithMargin>
          <RegularParagraph style={{ margin: "2px 0 7px" }}>
            <a href={product_url} target={"_blank"} rel="noreferrer">
              {product_url}
            </a>
          </RegularParagraph>
        </div>
      </TwoColumGrid>
    </AccordionCardWithEditFeatures>
  );
};
